import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, EmptyState, ListGroup, ListRow, MoneyField, Skeleton, Switch, TextField } from '@uplink/ui';
import { useState } from 'react';
import { errorText, useTRPC } from '../api.ts';
import { parseUsdInput } from '../format.ts';
import { usePrefs } from '../prefs.tsx';
import { haptic } from '../telegram.ts';

/** Promo codes: a fixed bonus to the balance, once per user, with an optional use limit and lifetime. */
export function AdminPromosScreen() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { usd, date } = usePrefs();
  const list = useQuery(trpc.admin.promoCodes.queryOptions());
  const [code, setCode] = useState('');
  const [amount, setAmount] = useState('5');
  const [maxUses, setMaxUses] = useState('');
  const [days, setDays] = useState('');
  const cents = parseUsdInput(amount);
  const uses = maxUses.trim() ? Number(maxUses) : null;
  const lifetime = days.trim() ? Number(days) : null;
  const valid =
    /^[A-Za-z0-9_-]{3,32}$/.test(code.trim()) &&
    !!cents &&
    (uses === null || (Number.isInteger(uses) && uses > 0)) &&
    (lifetime === null || (Number.isInteger(lifetime) && lifetime > 0));

  const refresh = () => queryClient.invalidateQueries(trpc.admin.promoCodes.pathFilter());
  const create = useMutation(
    trpc.admin.createPromoCode.mutationOptions({
      onSuccess: () => {
        haptic.success();
        setCode('');
        refresh();
      },
      onError: () => haptic.error(),
    }),
  );
  const toggle = useMutation(trpc.admin.setPromoCodeActive.mutationOptions({ onSuccess: refresh }));

  return (
    <main className="ul-screen">
      <h1 className="ul-screen__title">Промокоды</h1>

      <section className="ul-stack">
        <div className="ul-group__header">Новый промокод</div>
        <TextField
          id="promo-code"
          label="Код"
          value={code}
          onChange={(v) => setCode(v.toUpperCase())}
          placeholder="WELCOME5"
          hint="Латиница, цифры, «-» и «_». Пользователь вводит его в приложении."
        />
        <MoneyField
          id="promo-amount"
          label="Бонус"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />
        <div className="ul-row-actions">
          <TextField
            id="promo-uses"
            label="Активаций"
            inputMode="numeric"
            value={maxUses}
            onChange={setMaxUses}
            placeholder="без лимита"
          />
          <TextField
            id="promo-days"
            label="Дней действует"
            inputMode="numeric"
            value={days}
            onChange={setDays}
            placeholder="бессрочно"
          />
        </div>
        {create.isError ? (
          <div className="ul-error" role="alert">
            {errorText(create.error)}
          </div>
        ) : null}
        <Button
          block
          disabled={!valid}
          loading={create.isPending}
          onClick={() =>
            create.mutate({ code: code.trim(), amountCents: cents!, maxUses: uses, days: lifetime })
          }
        >
          {valid ? `Создать ${code.trim()} на ${usd(cents!)}` : 'Заполните код и сумму'}
        </Button>
      </section>

      {list.isPending ? (
        <Skeleton height={200} />
      ) : list.isError ? (
        <div className="ul-error" role="alert">
          {errorText(list.error)}
        </div>
      ) : list.data.length === 0 ? (
        <EmptyState
          icon="gift"
          title="Промокодов пока нет"
          text="Создайте первый: он появится здесь со счётчиком активаций"
        />
      ) : (
        <ListGroup header="Все промокоды">
          {list.data.map((p) => (
            <ListRow
              key={p.code}
              icon="gift"
              mono
              title={p.code}
              subtitle={[
                `+${usd(p.amountCents)}`,
                `использован ${p.used}${p.maxUses ? ` из ${p.maxUses}` : ''}`,
                p.expiresAt ? `до ${date(p.expiresAt)}` : 'бессрочный',
              ].join(' · ')}
              trailing={
                <Switch
                  checked={p.active}
                  label={`Промокод ${p.code} активен`}
                  onChange={(active) => toggle.mutate({ code: p.code, active })}
                />
              }
            />
          ))}
        </ListGroup>
      )}
    </main>
  );
}
